import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';
import classnames from 'classnames';
import { Form } from '@sparefoot/react-core';
import loadable from '@loadable/component';

import { Button } from 'components/core/Button';
import { GoogleAutocomplete } from 'components/core/GoogleAutocomplete';
import defaultUnitFilters from 'config/filters';
import usePrevious from 'hooks/usePrevious';

const IconDropdown = loadable(
	() =>
		import(
			/* webpackChunkName: "IconDropdown" */ 'components/core/IconDropdown'
		),
	{ resolveComponent: (mod) => mod.IconDropdown },
);

import './SearchBox.scss';
import { trackCTAClick } from '../../core/library/tracking-utils/tracking/clickTrackingUtils';

export function SearchBox({
	buttonText = 'Find Units',
	onSearch,
	autoSearch,
	id,
	icon,
	inputLabel,
	useBrowserLocation,
	setSearchAmenities,
	location: locationProp,
	locationParams: locationParamsProp,
	hideAutocompleteOnScroll = false,
}) {
	const initialLocation = locationParamsProp?.location || locationProp || '';
	const [locationParams, setLocationParams] = useState({
		location: initialLocation,
	});
	const [instance, setInstance] = useState(null);
	const [selectedLabel, setSelectedLabel] = useState('Self-Storage');
	const [showOptions, setShowOptions] = useState(false);

	const prevLocation = usePrevious(locationProp);
	const prevLocationParams = usePrevious(locationParamsProp);

	const handleGoogle = useCallback((params) => {
		setLocationParams(params);
	}, []);

	const getRef = useCallback((inst) => {
		setInstance(inst);
	}, []);

	const toggleShowOptions = useCallback((value) => {
		if (typeof value === 'boolean') {
			setShowOptions(value);
		} else {
			setShowOptions((prev) => !prev);
		}
	}, []);

	const handleClickOutside = useCallback((event) => {
		const dropdownEl = event.target.closest?.('.icon-dropdown');
		if (!dropdownEl) {
			setShowOptions(false);
		}
	}, []);

	const handleSearch = useCallback(
		(event) => {
			event.preventDefault();

			let searchParams = locationParams;

			// use the inputs value if not selected from autocomplete
			if (
				!locationParams.locationSource &&
				instance &&
				instance.value !== 'undefined'
			) {
				searchParams = { location: instance.value };
			}
			handleGoogle(searchParams);
			trackCTAClick(
				'search-box-id',
				'Search Button',
				'Find Units',
				window.location.href,
			);
			return onSearch({ locationParams: searchParams });
		},
		[handleGoogle, instance, locationParams, onSearch],
	);

	useEffect(() => {
		// Update internal state if parent location prop changed (legacy path)
		if (!isEqual(locationProp, prevLocation) && locationProp) {
			handleGoogle({ location: locationProp });
		}
	}, [locationProp, prevLocation, handleGoogle]);

	useEffect(() => {
		// Update internal state if parent locationParams prop changed (new path)
		if (
			!isEqual(locationParamsProp, prevLocationParams) &&
			locationParamsProp?.location
		) {
			handleGoogle({ location: locationParamsProp.location });
		}
		if (
			autoSearch &&
			!isEqual(locationParamsProp, prevLocationParams) &&
			locationParamsProp?.locationSource
		) {
			onSearch({ locationParams: locationParamsProp });
		}
	}, [
		locationParamsProp,
		prevLocationParams,
		autoSearch,
		onSearch,
		handleGoogle,
	]);

	useEffect(() => {
		if (showOptions) {
			document.addEventListener('mousedown', handleClickOutside);
		}
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [showOptions, handleClickOutside]);

	const { options } = defaultUnitFilters.searchTypes;
	return (
		<Form
			action="/search"
			className={classnames(icon && 'icon-search-box', 'search-box')}
			successLabel="success"
			failureLabel="fail"
			onSubmit={handleSearch}
		>
			{icon}
			<GoogleAutocomplete
				hideOnScroll={hideAutocompleteOnScroll}
				placeholder={inputLabel}
				className="location-input"
				onChange={handleGoogle}
				getRef={getRef}
				id={id}
				value={locationParams.location}
				name="location"
				useBrowserLocation={useBrowserLocation}
			/>
			{setSearchAmenities && (
				<IconDropdown
					setSearchAmenities={setSearchAmenities}
					setSelectedLabel={setSelectedLabel}
					options={options}
					onSelect={setSearchAmenities}
					selected={null}
					showOptions={showOptions}
					setShowOptions={toggleShowOptions}
					selectedLabel={selectedLabel}
				/>
			)}
			<Button
				primary
				type="submit"
				className="search-button"
				disabled={!locationParams.location}
			>
				{buttonText}
			</Button>
		</Form>
	);
}

SearchBox.propTypes = {
	onSearch: PropTypes.func.isRequired,
	autoSearch: PropTypes.bool,
	id: PropTypes.string,
	inputLabel: PropTypes.string,
	location: PropTypes.string,
	locationParams: PropTypes.shape({
		location: PropTypes.string,
		locationSource: PropTypes.string,
	}),
	buttonText: PropTypes.string,
	useBrowserLocation: PropTypes.bool,
	setSearchAmenities: PropTypes.func,
	icon: PropTypes.node,
	hideAutocompleteOnScroll: PropTypes.bool,
};

export default SearchBox;
